import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2 } from 'lucide-react';
import DataTable from '../DataTable';
import IconButton from '../common/IconButton';
import ConfirmDialog from '../ConfirmDialog';
import ExpensesSummaryCards from './ExpensesSummaryCards';
import ExpensesFilterBar, { ExpensesFilterValues } from './ExpensesFilterBar';
import ExpenseFormModal from './ExpenseFormModal';
import {
  generalExpensesApi,
  GeneralExpenseDto,
  ExpenseCategoryDto,
  GeneralExpenseSummaryDto,
  formatCurrency,
  formatDate,
} from '../../services/generalExpensesService';

const Container = styled.div`
  padding: 24px 0;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  background: var(--primary-color);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--primary-hover);
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

const NotesCell = styled.span`
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;

const PAGE_SIZE = 20;

const GeneralExpensesTab: React.FC = () => {
  const [expenses, setExpenses] = useState<GeneralExpenseDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<ExpensesFilterValues>({
    categoryId: null,
    paymentMethod: null,
    dateFrom: '',
    dateTo: '',
  });
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState<ExpenseCategoryDto[]>([]);
  const [summary, setSummary] = useState<GeneralExpenseSummaryDto | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<GeneralExpenseDto | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<GeneralExpenseDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  // debounce לחיפוש (~300ms)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const loadExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const result = await generalExpensesApi.list({
        page,
        pageSize: PAGE_SIZE,
        searchTerm: debouncedSearch || undefined,
        categoryId: filters.categoryId ?? undefined,
        paymentMethod: filters.paymentMethod ?? undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      });
      setExpenses(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      toast.error('שגיאה בטעינת ההוצאות');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filters]);

  // שינוי פילטר → איפוס לעמוד הראשון
  const handleFiltersChange = (next: ExpensesFilterValues) => {
    setFilters(next);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ categoryId: null, paymentMethod: null, dateFrom: '', dateTo: '' });
    setPage(1);
  };

  const loadCategories = useCallback(async () => {
    try {
      const cats = await generalExpensesApi.getCategories();
      setCategories(cats);
    } catch {
      // לא קריטי לטעינת הטאב
    }
  }, []);

  // הסיכום משקף את אותו סינון כמו הטבלה (ללא עימוד)
  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const data = await generalExpensesApi.getSummary({
        searchTerm: debouncedSearch || undefined,
        categoryId: filters.categoryId ?? undefined,
        paymentMethod: filters.paymentMethod ?? undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      });
      setSummary(data);
    } catch {
      // הסיכום אינו חוסם את הטבלה
    } finally {
      setSummaryLoading(false);
    }
  }, [debouncedSearch, filters]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const refreshAll = () => {
    loadExpenses();
    loadSummary();
  };

  const handleAdd = () => {
    setEditingExpense(null);
    setIsFormOpen(true);
  };

  const handleEdit = (expense: GeneralExpenseDto) => {
    setEditingExpense(expense);
    setIsFormOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await generalExpensesApi.remove(deleteTarget.id);
      toast.success('ההוצאה נמחקה בהצלחה');
      setDeleteTarget(null);
      // אם נמחק הפריט האחרון בעמוד - חוזרים עמוד אחורה
      if (expenses.length === 1 && page > 1) {
        setPage(p => p - 1);
      } else {
        refreshAll();
      }
    } catch {
      toast.error('שגיאה במחיקת ההוצאה');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { key: 'categoryName', title: 'קטגוריה' },
    {
      key: 'amount',
      title: 'סכום',
      render: (value: number) => formatCurrency(value),
    },
    {
      key: 'expenseDate',
      title: 'תאריך',
      render: (value: string) => formatDate(value),
    },
    {
      key: 'notes',
      title: 'הערות',
      render: (value: string) => (value ? <NotesCell title={value}>{value}</NotesCell> : '-'),
    },
    { key: 'paymentMethodName', title: 'אופן תשלום' },
    {
      key: 'actions',
      title: 'פעולות',
      width: '120px',
      render: (_: any, record: GeneralExpenseDto) => (
        <RowActions>
          <IconButton icon={Edit} title="עריכה" variant="primary" onClick={() => handleEdit(record)} />
          <IconButton icon={Trash2} title="מחיקה" variant="danger" onClick={() => setDeleteTarget(record)} />
        </RowActions>
      ),
    },
  ];

  return (
    <Container>
      <ExpensesSummaryCards summary={summary} loading={summaryLoading} />

      <ExpensesFilterBar
        categories={categories}
        filters={filters}
        onChange={handleFiltersChange}
        onClear={handleClearFilters}
      />

      <DataTable<GeneralExpenseDto>
        data={expenses}
        columns={columns}
        loading={loading}
        searchPlaceholder="חיפוש לפי קטגוריה או הערות..."
        onSearch={setSearchInput}
        actions={
          <AddButton onClick={handleAdd}>
            <Plus size={18} />
            הוסף הוצאה חדשה
          </AddButton>
        }
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: totalCount,
          onChange: (newPage) => setPage(newPage),
        }}
      />

      <ExpenseFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSaved={refreshAll}
        expense={editingExpense}
        categories={categories}
        onCategoriesChanged={loadCategories}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת הוצאה"
        message={
          deleteTarget
            ? `האם למחוק את ההוצאה "${deleteTarget.categoryName}" בסך ${formatCurrency(deleteTarget.amount)}?`
            : ''
        }
        confirmText="מחק"
        cancelText="ביטול"
        type="danger"
        loading={deleting}
      />
    </Container>
  );
};

export default GeneralExpensesTab;
